import type { ComponentKind, Marking, Tracking } from './types'

/**
 * How a serialized part carries its identity in the physical world (§10.1).
 *
 * §10 rests on one sentence — point a phone at a thing and be on its page — and
 * the original answer to "what about parts too small for a label?" was the
 * first row of §10.1: do not serialize them, count them instead. That is the
 * right answer for fasteners and the wrong one for a 20 mm camera, which is a
 * swap unit with a life worth tracking and no face big enough for a
 * 25.4 × 12.7 mm label, because the back of it is a heatsink.
 *
 * So marking is a property of the **type**, not the instance: every one of
 * those cameras is marked the same way, and the decision belongs where an
 * engineer sets `kind` and `tracking`. Three values, and each says something
 * different about where the identity lives:
 *
 *  - `qr_label` — the §10.2 label is on the part. The default, and what
 *    everything ordered before this rule existed still is.
 *  - `text_only` — the serial is on the part as human-readable characters and
 *    nothing machine-readable: an engraving, a paint pen, a side label too
 *    narrow for a 9 mm code. Base32 with a check character is what makes this
 *    safe to type off the part (§10.1).
 *  - `none` — nothing on the part at all. The identity lives in the tree: a
 *    component's position under its parent names it exactly, which the model
 *    already relies on because a `UsageEvent` stores a root and a path and
 *    never a component id.
 *
 * What `none` costs is honest and worth stating: a loose one on the shelf
 * cannot be told from its neighbour by looking. The shelf answer is to mark the
 * bin rather than the part, and that is a warehouse decision, not a schema one.
 */

/** Where the serial comes from when one of these is received (§10.3). */
export type SerialSource =
  /** Read off the label by the camera, or typed if the label is scuffed. */
  | 'scan'
  /** Typed off the part by a person. There is no code to read. */
  | 'typed'
  /** Issued by Kronos. Nobody types it, because it is written nowhere. */
  | 'allocated'

export function serialSource(marking: Marking): SerialSource {
  switch (marking) {
    case 'qr_label':
      return 'scan'
    case 'text_only':
      return 'typed'
    case 'none':
      return 'allocated'
  }
}

/**
 * True when receiving one means a person supplies the serial, either by
 * scanning it or by reading it off the part.
 *
 * The intake loop of §10.3 exists only for these. An unmarked type has nothing
 * to scan and nothing to read, so its receive is a count — the same question a
 * quantity type is asked, with the difference that each one still becomes a
 * component with its own history.
 */
export function intakeNeedsSerial(marking: Marking): boolean {
  return serialSource(marking) !== 'allocated'
}

export type MarkingRefusal =
  /** A counted item has no identity to mark. */
  | 'counted_cannot_be_marked'
  /** §10.1: the nameplate is the one marking that must survive everything. */
  | 'robot_needs_a_nameplate'

/**
 * The two ways a marking can contradict the rest of the type.
 *
 * Both are also CHECK constraints in `011_marking.sql`, deliberately: this
 * function hides controls in Admin and the database is what actually holds the
 * line, the same split `can()` has against the server.
 */
export function checkMarking(
  input: { readonly tracking: Tracking; readonly isRoot: boolean; readonly marking: Marking },
): { readonly ok: true } | { readonly ok: false; readonly reason: MarkingRefusal } {
  // A bag of screws has no serial, so there is nothing a marking could describe.
  // Forcing `none` keeps one way to say that rather than three.
  if (input.tracking === 'quantity' && input.marking !== 'none') {
    return { ok: false, reason: 'counted_cannot_be_marked' }
  }
  // A robot is the object that must stay identifiable when everything else has
  // failed, and §10.1 gives it a laser-engraved plate for exactly that reason.
  // An unmarked robot is one nobody can scan, which is §10's premise inverted.
  if (input.isRoot && input.marking !== 'qr_label') {
    return { ok: false, reason: 'robot_needs_a_nameplate' }
  }
  return { ok: true }
}

export function describeMarkingRefusal(reason: MarkingRefusal): string {
  switch (reason) {
    case 'counted_cannot_be_marked':
      return 'a counted item has no serial to mark — set it to serialized first'
    case 'robot_needs_a_nameplate':
      return 'a robot carries an engraved nameplate, so its marking cannot be removed (§10.1)'
  }
}

/** The marking a type should get when its tracking changes under it. */
/**
 * What a new type should be marked as, before anybody thinks about it.
 *
 * **An assembly is identified through its children, so it does not need a
 * label.** Scan any actuator inside an arm and the breadcrumb walks up to the
 * arm and on to the robot — `ComponentPage.ancestors` is that walk, and it is
 * the interaction §10 rests on. Printing a code for the arm as well is a label
 * to peel, stick, and keep legible for five years in order to learn something
 * the tree already knows.
 *
 * So the default turns on `kind`, not on `tracking` alone:
 *
 *  - `make_internal` — assembled here, therefore has children to be found
 *    through. Unmarked. It still draws a serial from the same pool, so the
 *    identity scheme is unchanged and an arm somebody *does* label just works.
 *  - `make_external` and `buy` — leaves. A vendor's plate and a bought camera
 *    have nothing inside them to scan, so the identity has to be on the part.
 *  - a robot — `qr_label` regardless, and `checkMarking` refuses anything
 *    else: the engraved nameplate is the one object that must stay
 *    identifiable when everything else has failed (§10.1).
 *
 * **The hole this leaves, and it is a real one:** an assembly whose bill of
 * materials has no *serialized* child — a frame of tube and fasteners — has
 * nothing inside it to scan either, so unmarked makes it unreachable. The
 * default cannot see that, because a type is created before its BOM exists.
 * `unmarkedIsReachable` is the check for it, and it belongs where the BOM is
 * edited rather than here.
 */
export function defaultMarkingFor(shape: {
  readonly tracking: Tracking
  /** Omitted when the caller genuinely does not know yet, which defaults to
   *  labelled — the conservative direction, since a label can be ignored and a
   *  missing one cannot be invented later. */
  readonly kind?: ComponentKind
  readonly isRoot?: boolean
}): Marking {
  if (shape.tracking === 'quantity') return 'none'
  if (shape.isRoot === true) return 'qr_label'
  return shape.kind === 'make_internal' ? 'none' : 'qr_label'
}

/**
 * Can an unmarked assembly actually be found?
 *
 * Only if something inside it carries an identity. A serialized child is a
 * position you scan a part into, so one of those is enough: scan it, walk up.
 * An assembly of nothing but counted items — tube, fasteners, harness — has no
 * scannable content, and unmarked it becomes a component that exists in the
 * database and cannot be reached from the physical world.
 *
 * Returns true for a marked type without looking at the lines: the label
 * answers the question by itself.
 */
export function unmarkedIsReachable(
  marking: Marking,
  childTrackings: readonly Tracking[],
): boolean {
  if (marking !== 'none') return true
  return childTrackings.includes('serialized')
}
